import React from 'react';
import Link from 'next/link';
import omit from 'lodash/omit';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import GeneralChart from '../GeneralChart';
import GeneralMenu from '../../navigation/GeneralMenu';
import SvgLight from '../SvgLight';
import { filterArrayWithValidItems } from '@/utils/seo-content';

interface Props {
  show: boolean;
  strategyData: Record<string, any>;
  submissionSuccessRateData: Record<string, any>;
  precautionsData: Record<string, any>;
}

export const COMPANY_JOB_SEEKING_GUIDE_ITEMS = [
  {
    name: '不同职业阶段，你应如何制定求职策略？',
    id: 'company-job-seeking-guide_job-seeking-strategy',
  },
  {
    name: '如何提高投递成功率？',
    id: 'company-job-seeking-guide_improve-submission-success-rate',
  },
  {
    name: '求职注意事项',
    id: 'company-job-seeking-guide_job-seeking-precautions',
  },
];

const OutlinedTag = styled('b')(() => ({
  border: '1px solid #d4d2d0',
  padding: '4px 8px',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: '300',
  lineHeight: '100%',
  letterSpacing: '0%',
}));

const JobSeekingStrategySwitcher = ({
  data,
}: {
  data: Record<string, any>;
}) => {
  const stages = ['应届毕业生', '初中级社招', '资深人才'];
  const [activeStage, setActiveStage] = React.useState(stages[0]);
  if (Object.keys(data).length === 0) return null;
  return (
    <>
      <div className="flex gap-10 mb-2">
        {stages.map((stage, index) => (
          <span
            key={index}
            onClick={() => setActiveStage(stage)}
            className={`transition-colors cursor-pointer font-semibold ${
              activeStage === stage
                ? 'border-b border-primary border-solid'
                : 'text-web-grey-600 hover:text-primary'
            }`}
          >
            {stage}
          </span>
        ))}
      </div>
      {stages.map((stage) => (
        <div key={stage} hidden={activeStage !== stage}>
          <p className="mb-2">{data[stage]['总体说明']}</p>
          <h4 className="font-semibold mb-2">求职策略建议</h4>
          <ul className="list-inside !ml-2">
            {data[stage]['求职策略建议'].map((item: string, index: number) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
};

const JobSeekingGuideTabPanel = (props: Props) => {
  const { show, strategyData, submissionSuccessRateData, precautionsData } =
    props;
  const specialOpportunityChannel = filterArrayWithValidItems(
    submissionSuccessRateData['特殊机会通道'],
  );
  const applicationChannel = filterArrayWithValidItems(
    submissionSuccessRateData['投递渠道'],
  );
  const graspOfTiming = filterArrayWithValidItems(
    submissionSuccessRateData['时机把握'],
  );
  const urbanOpportunityDistribution = filterArrayWithValidItems(
    submissionSuccessRateData['城市机会分布'],
  );
  const potentialOpportunities = filterArrayWithValidItems(
    submissionSuccessRateData['不同岗位类别潜在机会'],
  );
  const strategicRecommendations = filterArrayWithValidItems(
    submissionSuccessRateData['策略建议'],
  );
  const basicInterviewQuestions = filterArrayWithValidItems(
    precautionsData['面试对岗位的基础问题'],
  );
  const warningSigns = filterArrayWithValidItems(
    precautionsData['要警惕的信号'],
  );
  const negotiationPoints = filterArrayWithValidItems(
    precautionsData['薪资与合同谈判要点'],
  );
  const actionList = filterArrayWithValidItems(
    precautionsData['入职前后关键动作清单'],
  );
  return (
    <div hidden={!show} className="relative">
      <GeneralMenu menuList={COMPANY_JOB_SEEKING_GUIDE_ITEMS} />
      <h2 className="text-xl font-semibold">
        作为求职者，如何准备这家公司的求职
      </h2>
      {/*<GeneralChart id={COMPANY_JOB_SEEKING_GUIDE_ITEMS[0].id} padding="p-0">*/}
      {/*  <div className="grid grid-cols-2 relative">*/}
      {/*    <div className="pl-10 pt-8 pb-8 pr-5 border-r border-white-smoke flex flex-col">*/}
      {/*      <p className="font-semibold mb-2">7月新增岗位</p>*/}
      {/*      <p className="text-4.5xl font-semibold mb-2">78</p>*/}
      {/*      <p className="text-sm mb-4">*/}
      {/*        对比上月：新增岗位+6 | 对比去年：岗位增长+15%*/}
      {/*      </p>*/}
      {/*      <p className="text-sm">*/}
      {/*        较去年同期上升 15%，但较三年前高峰仍低。说明公司在谨慎扩张。*/}
      {/*      </p>*/}
      {/*    </div>*/}
      {/*    <div className="px-10 py-8 pr-14">*/}
      {/*      <p className="font-semibold mb-3">往年招聘规模</p>*/}
      {/*      <p className="flex gap-2.5 mb-3">*/}
      {/*        <OutlinedTag>AI 岗位 +40%</OutlinedTag>*/}
      {/*        <OutlinedTag>春秋招聘高峰期</OutlinedTag>*/}
      {/*        <OutlinedTag>内推成功率60%</OutlinedTag>*/}
      {/*      </p>*/}
      {/*      <ul className="list-inside !ml-2 text-sm leading-6.5 tracking-normal">*/}
      {/*        <li>*/}
      {/*          2024 年招聘总数：8,000-10,000人，较 2023*/}
      {/*          年增长：15%（主要增长来自云服务、AI、信创领域）*/}
      {/*        </li>*/}
      {/*        <li>社会招聘 vs 校园招聘比例：7:3</li>*/}
      {/*        <li>用人周期：上半年以校招为主，下半年偏社招补充项目人手。</li>*/}
      {/*      </ul>*/}
      {/*    </div>*/}
      {/*    <p className="absolute left-6 bottom-5 text-s text-web-grey-600">*/}
      {/*      数据由各大平台公开数据统计分析而来，仅供参考。*/}
      {/*    </p>*/}
      {/*  </div>*/}
      {/*</GeneralChart>*/}
      <GeneralChart
        id={COMPANY_JOB_SEEKING_GUIDE_ITEMS[0].id}
        title={COMPANY_JOB_SEEKING_GUIDE_ITEMS[0].name}
        tip={strategyData['tip']}
        tipBgColor="bg-web-purple-100"
      >
        <div className="mt-4">
          <JobSeekingStrategySwitcher data={omit(strategyData, 'tip')} />
        </div>
        <Link href={`/jianli/evaluation/`}>
          <Box
            sx={{
              borderRadius: '8px',
              background: 'linear-gradient(180deg, #3F73D3 0%, #237EA3 95.62%)',
              p: '1px',
              marginTop: '8px',
            }}
          >
            <Box className="flex items-center gap-2.5 px-4 py-2 bg-white text-sm rounded-[7px] cursor-pointer">
              <SvgLight />
              你的简历写的能让 HR 心动吗？
              <span className="underline">上传简历立即诊断</span>
            </Box>
          </Box>
        </Link>
      </GeneralChart>
      <GeneralChart
        id={COMPANY_JOB_SEEKING_GUIDE_ITEMS[1].id}
        title={COMPANY_JOB_SEEKING_GUIDE_ITEMS[1].name}
        tip={submissionSuccessRateData['tip']}
        tipBgColor="bg-web-purple-100"
      >
        {applicationChannel.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">投递渠道</h4>
            <ul className="list-inside !ml-2 mb-4">
              {applicationChannel.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {graspOfTiming.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">时机把握</h4>
            <ul className="list-inside !ml-2 mb-4">
              {graspOfTiming.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {urbanOpportunityDistribution.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">城市机会分布</h4>
            <ul className="list-inside !ml-2 mb-4">
              {urbanOpportunityDistribution.map(
                (item: string, index: number) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ul>
          </>
        )}
        {potentialOpportunities.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">不同岗位类别的潜在机会</h4>
            <ul className="list-inside !ml-2 mb-4">
              {potentialOpportunities.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {specialOpportunityChannel.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">特殊机会通道</h4>
            <ul className="list-inside !ml-2 mb-4">
              {specialOpportunityChannel.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {strategicRecommendations.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">策略建议</h4>
            <ul className="list-inside !ml-2">
              {strategicRecommendations.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
      </GeneralChart>
      <GeneralChart
        id={COMPANY_JOB_SEEKING_GUIDE_ITEMS[2].id}
        title={COMPANY_JOB_SEEKING_GUIDE_ITEMS[2].name}
        tip={precautionsData['tip']}
        tipBgColor="bg-web-purple-100"
      >
        {basicInterviewQuestions.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">面试时你应问的基础问题</h4>
            <ul className="list-inside !ml-2 mb-4">
              {basicInterviewQuestions.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {warningSigns.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">
              要警惕的信号（面试/offer 阶段）
            </h4>
            <ul className="list-inside !ml-2 mb-4">
              {warningSigns.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {negotiationPoints.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">薪资与合同谈判要点</h4>
            <ul className="list-inside !ml-2 mb-4">
              {negotiationPoints.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {actionList.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">入职前后关键动作清单</h4>
            <ul className="list-inside !ml-2">
              {actionList.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
      </GeneralChart>
    </div>
  );
};

export default JobSeekingGuideTabPanel;
